/**
 * IBKR Pro commission estimates for US stocks — the approval cards show every scenario after fees.
 * Rates: interactivebrokers.com/en/pricing/commissions-stocks.php (checked 2026-09-27). The paper
 * account's whatIf for SELL 2 STP returned $1.00457 = $1.00 minimum + SEC + FINRA TAF + CAT (Fixed).
 */
/** 'robinhood' is not a choice in Settings: a Robinhood worker / order always uses it. */
export type CommissionPlan = 'fixed' | 'tiered' | 'robinhood'

/** The IBKR plans a user picks between in Settings. */
export const COMMISSION_PLANS: CommissionPlan[] = ['fixed', 'tiered']

export const COMMISSION_PLAN_LABEL: Record<CommissionPlan, string> = { fixed: 'IBKR Pro Fixed', tiered: 'IBKR Pro Tiered', robinhood: 'Robinhood (commission-free)' }

const RATES: Record<CommissionPlan, { perShare: number; min: number; venuePerShare: number }> = {
  // Exchange and most regulatory fees are included; SEC/FINRA/CAT are passed through.
  fixed: { perShare: 0.005, min: 1, venuePerShare: 0 },
  // ≤ 300k shares a month. Exchange + clearing are extra: estimated for an order that takes
  // liquidity (≈ $0.003 exchange + $0.0002 clearing + pass-through); a resting limit may earn a rebate.
  tiered: { perShare: 0.0035, min: 0.35, venuePerShare: 0.0033 },
  // No commission. A sale carries the regulatory fees only (SEC on sales above $500, FINRA TAF on
  // sales above 50 shares — robinhood.com/us/en/support/articles/trading-fees-on-robinhood).
  robinhood: { perShare: 0, min: 0, venuePerShare: 0 },
}
const SEC_PER_USD_SOLD = 0.0000206
const FINRA_TAF_PER_SHARE_SOLD = 0.000195
const CAT_PER_SHARE = 0.000003
const MAX_PCT_OF_VALUE = 0.01

export const minCommission = (plan: CommissionPlan) => RATES[plan].min

export function isCommissionPlan(v: unknown): v is CommissionPlan {
  return v === 'fixed' || v === 'tiered'
}

/** Estimated cost of ONE filled order: the commission (per-order minimum, 1% cap) plus pass-through fees. */
export function orderFee(plan: CommissionPlan, action: 'BUY' | 'SELL', shares: number, price: number): number {
  if (!(shares > 0) || !(price > 0)) return 0
  const r = RATES[plan]
  const value = shares * price
  const commission = Math.min(Math.max(shares * r.perShare, r.min), value * MAX_PCT_OF_VALUE)
  if (plan === 'robinhood') return action === 'SELL' ? (value > 500 ? value * SEC_PER_USD_SOLD : 0) + (shares > 50 ? shares * FINRA_TAF_PER_SHARE_SOLD : 0) : 0
  const regulatory = (action === 'SELL' ? value * SEC_PER_USD_SOLD + shares * FINRA_TAF_PER_SHARE_SOLD : 0) + shares * CAT_PER_SHARE
  return commission + shares * r.venuePerShare + regulatory
}

/** True when the order pays the plan's per-order minimum (the share-based fee is below it). */
export function atMinimum(plan: CommissionPlan, shares: number, price: number): boolean {
  const r = RATES[plan]
  return shares * r.perShare < r.min && shares * price * MAX_PCT_OF_VALUE > r.min
}
